// The numbers on the two Messages rows (PRD 68 §5.1, §5.2).
//
// Asked when the row is drawn and again whenever the window comes back into
// focus — the same rhythm as the Notifications badge, so a reply that lands
// while somebody is in another tab is waiting on the row when they return,
// without the sidebar polling all day. The thread screens refresh it after
// they open a thread, which is what reads it.
//
// ONLY THE SIDES THAT ARE DRAWN ARE ASKED. A client with no accountant has no
// client inbox to count, and the client count is per business, so it is asked
// again when the active business changes.
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  UnreadMessageCounts,
  clearUnreadMessages,
  getUnreadMessageCounts,
  refreshClientUnreadMessages,
  refreshFirmUnreadMessages,
  subscribeUnreadMessageCounts,
} from '../services/messageUnreadStore';

const NONE: UnreadMessageCounts = { firm: 0, client: 0 };

export function useUnreadMessages(
  { firm, client, businessId }: { firm: boolean; client: boolean; businessId?: string | null },
): UnreadMessageCounts {
  const { user } = useAuth();
  const authToken = user?.authToken;
  const [counts, setCounts] = useState<UnreadMessageCounts>(getUnreadMessageCounts);

  useEffect(() => {
    const unsubscribe = subscribeUnreadMessageCounts(setCounts);
    if (!authToken) {
      clearUnreadMessages();
      return unsubscribe;
    }

    const refresh = () => {
      if (firm) void refreshFirmUnreadMessages();
      if (client && businessId) void refreshClientUnreadMessages();
    };
    refresh();
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      unsubscribe();
    };
  }, [authToken, firm, client, businessId]);

  if (!authToken) return NONE;
  return { firm: firm ? counts.firm : 0, client: client && businessId ? counts.client : 0 };
}
